import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { 
  Package, Search, Filter, Download, Box, Tag,
  ChevronDown, Layers, DollarSign, Plus, Upload, Trash2, Edit, X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import PermButton from "@/components/PermButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { uploadWithReceipt } from "@/lib/bulkUploadReceipt";
import useAuthStore from "../store/authStore";
import Pagination from "../components/Pagination";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DemandSKUView = () => {
  // Get token from zustand auth store
  const token = useAuthStore((state) => state.token);
  
  const getHeaders = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const [activeTab, setActiveTab] = useState("bidso");
  
  // Data
  const [bidsoSkus, setBidsoSkus] = useState([]);
  const [buyerSkus, setBuyerSkus] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [buyers, setBuyers] = useState([]);
  
  // Filters
  const [bidsoFilters, setBidsoFilters] = useState({
    vertical_id: "",
    brand_id: "",
    model_id: "",
    search: ""
  });
  const [buyerFilters, setBuyerFilters] = useState({
    vertical_id: "",
    brand_id: "",
    model_id: "",
    buyer_id: "",
    search: ""
  });
  
  // Loading
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  
  // Pagination for Bidso SKUs
  const [bidsoPage, setBidsoPage] = useState(1);
  const [bidsoPageSize, setBidsoPageSize] = useState(50);
  const [bidsoTotal, setBidsoTotal] = useState(0);
  const [bidsoTotalPages, setBidsoTotalPages] = useState(1);
  
  // Pagination for Buyer SKUs
  const [buyerPage, setBuyerPage] = useState(1);
  const [buyerPageSize, setBuyerPageSize] = useState(50);
  const [buyerTotal, setBuyerTotal] = useState(0);
  const [buyerTotalPages, setBuyerTotalPages] = useState(1);
  
  // Price Master State
  const [prices, setPrices] = useState([]);
  const [pricesTotal, setPricesTotal] = useState(0);
  // Page-state for the Price Master table — 100 rows per page, paginated
  // server-side so we never fetch the full set into the table view.
  const PRICES_PER_PAGE = 100;
  const [pricePage, setPricePage] = useState(1);
  const pricesTotalPages = Math.max(1, Math.ceil(pricesTotal / PRICES_PER_PAGE));
  const [priceFilters, setPriceFilters] = useState({ customer_id: "", search: "" });
  const [showPriceDialog, setShowPriceDialog] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  const [priceForm, setPriceForm] = useState({
    customer_id: "",
    buyer_sku_id: "",
    unit_price: "",
    currency: "INR",
    notes: ""
  });
  const priceFileRef = useRef(null);

  // ─── Realisation Master state (Aug 2026) ─────────────────────────
  // Same shape as Price Master, sits right beside it in this SKU
  // Catalog view — same team, same auth, same rules. Only marketplace
  // customers where invoiced ≠ realised (Flipkart etc.) need rows here.
  const [realisations, setRealisations] = useState([]);
  const [realisationsTotal, setRealisationsTotal] = useState(0);
  const REALISATIONS_PER_PAGE = 100;
  const [realisationPage, setRealisationPage] = useState(1);
  const realisationTotalPages = Math.max(1, Math.ceil(realisationsTotal / REALISATIONS_PER_PAGE));
  const [realisationFilters, setRealisationFilters] = useState({ customer_id: "" });
  const [showRealisationDialog, setShowRealisationDialog] = useState(false);
  const [editingRealisation, setEditingRealisation] = useState(null);
  const [realisationForm, setRealisationForm] = useState({
    customer_id: "",
    buyer_sku_id: "",
    realisation_price: "",
    currency: "INR",
    notes: "",
  });
  const realisationFileRef = useRef(null);

  useEffect(() => {
    fetchMasterData();
    // Fetch initial counts for tabs
    fetchInitialCounts();
  }, []);

  const fetchInitialCounts = async () => {
    try {
      // Get Bidso count
      const bidsoRes = await axios.get(`${API}/demand-hub/bidso-skus?page=1&page_size=1`, { headers: getHeaders() });
      if (bidsoRes.data.total !== undefined) {
        setBidsoTotal(bidsoRes.data.total);
      }
      
      // Get Buyer count
      const buyerRes = await axios.get(`${API}/skus/filtered?page=1&page_size=1`, { headers: getHeaders() });
      if (buyerRes.data.total !== undefined) {
        setBuyerTotal(buyerRes.data.total);
      }
    } catch (error) {
      console.error("Failed to fetch initial counts");
    }
  };

  useEffect(() => {
    if (activeTab === "bidso") {
      fetchBidsoSkus();
    } else if (activeTab === "buyer") {
      fetchBuyerSkus();
    } else if (activeTab === "prices") {
      fetchPrices();
    } else if (activeTab === "realisations") {
      fetchRealisations();
    }
  }, [activeTab, bidsoFilters, buyerFilters, priceFilters, realisationFilters, bidsoPage, bidsoPageSize, buyerPage, buyerPageSize, pricePage, realisationPage]);

  const fetchMasterData = async () => {
    try {
      const headers = getHeaders();
      const [vertRes, brandRes, modelRes, buyerRes] = await Promise.all([
        axios.get(`${API}/verticals`, { headers }),
        axios.get(`${API}/brands`, { headers }),
        axios.get(`${API}/models`, { headers }),
        axios.get(`${API}/buyers`, { headers })
      ]);
      setVerticals(vertRes.data || []);
      setBrands(brandRes.data || []);
      setModels(modelRes.data || []);
      setBuyers(buyerRes.data || []);
    } catch (error) {
      console.error("Failed to fetch master data");
    }
  };

  const fetchBidsoSkus = async () => {
    setLoading(true);
    try {
      let url = `${API}/demand-hub/bidso-skus?page=${bidsoPage}&page_size=${bidsoPageSize}`;
      if (bidsoFilters.vertical_id) url += `&vertical_id=${bidsoFilters.vertical_id}`;
      if (bidsoFilters.model_id) url += `&model_id=${bidsoFilters.model_id}`;
      if (bidsoFilters.search) url += `&search=${encodeURIComponent(bidsoFilters.search)}`;
      
      const res = await axios.get(url, { headers: getHeaders() });
      // Handle both paginated and non-paginated response formats
      if (res.data.items) {
        setBidsoSkus(res.data.items);
        setBidsoTotal(res.data.total || 0);
        setBidsoTotalPages(res.data.total_pages || 1);
      } else {
        setBidsoSkus(res.data || []);
        setBidsoTotal(res.data?.length || 0);
        setBidsoTotalPages(1);
      }
    } catch (error) {
      toast.error("Failed to fetch Bidso SKUs");
    } finally {
      setLoading(false);
    }
  };

  const fetchBuyerSkus = async () => {
    setLoading(true);
    try {
      let url = `${API}/skus/filtered?page=${buyerPage}&page_size=${buyerPageSize}`;
      if (buyerFilters.vertical_id) url += `&vertical_id=${buyerFilters.vertical_id}`;
      if (buyerFilters.brand_id) url += `&brand_id=${buyerFilters.brand_id}`;
      if (buyerFilters.model_id) url += `&model_id=${buyerFilters.model_id}`;
      if (buyerFilters.buyer_id) url += `&buyer_id=${buyerFilters.buyer_id}`;
      if (buyerFilters.search) url += `&search=${encodeURIComponent(buyerFilters.search)}`;
      
      const res = await axios.get(url, { headers: getHeaders() });
      // Handle both paginated and non-paginated response formats
      if (res.data.items) {
        setBuyerSkus(res.data.items);
        setBuyerTotal(res.data.total || 0);
        setBuyerTotalPages(res.data.total_pages || 1);
      } else {
        setBuyerSkus(res.data || []);
        setBuyerTotal(res.data?.length || 0);
        setBuyerTotalPages(1);
      }
    } catch (error) {
      toast.error("Failed to fetch Buyer SKUs");
    } finally {
      setLoading(false);
    }
  };

  // Pagination handlers for Bidso SKUs
  const handleBidsoPageChange = (page) => {
    setBidsoPage(page);
  };
  
  const handleBidsoPageSizeChange = (size) => {
    setBidsoPageSize(size);
    setBidsoPage(1);
  };
  
  // Pagination handlers for Buyer SKUs
  const handleBuyerPageChange = (page) => {
    setBuyerPage(page);
  };
  
  const handleBuyerPageSizeChange = (size) => {
    setBuyerPageSize(size);
    setBuyerPage(1);
  };
  
  // Reset pagination when filters change
  const handleBidsoFilterChange = (key, value) => {
    setBidsoFilters(prev => ({ ...prev, [key]: value }));
    setBidsoPage(1);
  };
  
  const handleBuyerFilterChange = (key, value) => {
    setBuyerFilters(prev => ({ ...prev, [key]: value }));
    setBuyerPage(1);
  };

  const handleDownload = async (type) => {
    setDownloading(true);
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();
      
      if (type === "bidso") {
        // Fetch ALL filtered Bidso SKUs (no pagination limit)
        let url = `${API}/demand-hub/bidso-skus?page=1&page_size=10000`;
        if (bidsoFilters.vertical_id) url += `&vertical_id=${bidsoFilters.vertical_id}`;
        if (bidsoFilters.model_id) url += `&model_id=${bidsoFilters.model_id}`;
        if (bidsoFilters.search) url += `&search=${encodeURIComponent(bidsoFilters.search)}`;
        
        const res = await axios.get(url, { headers: getHeaders() });
        const allBidsoSkus = res.data.items || res.data || [];
        
        // Prepare Bidso SKU data
        const headers = ['Bidso SKU ID', 'Name', 'Description', 'Vertical', 'Model', 'Status'];
        const data = [headers];
        
        allBidsoSkus.forEach(sku => {
          data.push([
            sku.bidso_sku_id || '',
            sku.name || '',
            sku.description || '',
            sku.vertical?.name || sku.vertical_code || '',
            sku.model?.name || sku.model_code || '',
            sku.status || 'ACTIVE'
          ]);
        });
        
        const ws = XLSX.utils.aoa_to_sheet(data);
        ws['!cols'] = [
          { wch: 20 }, { wch: 30 }, { wch: 40 }, { wch: 15 }, { wch: 20 }, { wch: 10 }
        ];
        XLSX.utils.book_append_sheet(wb, ws, 'Bidso SKUs');
        XLSX.writeFile(wb, `bidso_skus_${new Date().toISOString().slice(0,10)}.xlsx`);
        toast.success(`Downloaded ${allBidsoSkus.length} Bidso SKUs`);
        
      } else {
        // Fetch ALL filtered Buyer SKUs (no pagination limit)
        let url = `${API}/skus/filtered?page=1&page_size=10000`;
        if (buyerFilters.vertical_id) url += `&vertical_id=${buyerFilters.vertical_id}`;
        if (buyerFilters.brand_id) url += `&brand_id=${buyerFilters.brand_id}`;
        if (buyerFilters.model_id) url += `&model_id=${buyerFilters.model_id}`;
        if (buyerFilters.buyer_id) url += `&buyer_id=${buyerFilters.buyer_id}`;
        if (buyerFilters.search) url += `&search=${encodeURIComponent(buyerFilters.search)}`;
        
        const res = await axios.get(url, { headers: getHeaders() });
        const allBuyerSkus = res.data.items || res.data || [];
        
        // Prepare Buyer SKU data
        const headers = ['SKU ID', 'Description', 'Vertical', 'Brand', 'Model', 'Buyer', 'Bidso SKU', 'Status'];
        const data = [headers];
        
        allBuyerSkus.forEach(sku => {
          data.push([
            sku.sku_id || sku.buyer_sku_id || '',
            sku.description || sku.name || '',
            sku.vertical || sku.vertical_code || '',
            sku.brand || sku.brand_code || '',
            sku.model || sku.model_code || '',
            sku.buyer_name || '',
            sku.bidso_sku_id || '',
            sku.status || 'ACTIVE'
          ]);
        });
        
        const ws = XLSX.utils.aoa_to_sheet(data);
        ws['!cols'] = [
          { wch: 20 }, { wch: 40 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 20 }, { wch: 10 }
        ];
        XLSX.utils.book_append_sheet(wb, ws, 'Buyer SKUs');
        XLSX.writeFile(wb, `buyer_skus_${new Date().toISOString().slice(0,10)}.xlsx`);
        toast.success(`Downloaded ${allBuyerSkus.length} Buyer SKUs`);
      }
      
    } catch (error) {
      toast.error("Failed to download SKUs");
      console.error(error);
    } finally {
      setDownloading(false);
    }
  };

  const getFilteredModels = (verticalId) => {
    if (!verticalId) return models;
    return models.filter(m => m.vertical_id === verticalId);
  };

  const getVerticalName = (id) => verticals.find(v => v.id === id)?.name || '-';
  const getBrandName = (id) => brands.find(b => b.id === id)?.name || '-';
  const getModelName = (id) => models.find(m => m.id === id)?.name || '-';
  const getBuyerName = (id) => buyers.find(b => b.id === id)?.name || '-';

  // ============ Price Master Functions ============
  
  // Price Master state — we keep `pricesTotal` from the API so the tab
  // badge shows the TRUE total in the DB (not the in-memory array
  // length, which used to cap at 200).

  const fetchPrices = async () => {
    setLoading(true);
    try {
      // Server-side pagination — fetch only the current page (100 rows).
      // Full-set export uses a separate path that pages through everything.
      const params = new URLSearchParams();
      if (priceFilters.customer_id && priceFilters.customer_id !== "_all") {
        params.append("customer_id", priceFilters.customer_id);
      }
      params.append("active_only", "true");
      params.append("page_size", String(PRICES_PER_PAGE));
      params.append("page", String(pricePage));

      const res = await axios.get(`${API}/price-master?${params}`, { headers: getHeaders() });
      setPrices(res.data.prices || []);
      setPricesTotal(res.data.total ?? (res.data.prices?.length || 0));
    } catch (error) {
      toast.error("Failed to fetch prices");
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePrice = async () => {
    if (!priceForm.customer_id || !priceForm.buyer_sku_id || !priceForm.unit_price) {
      toast.error("Please fill all required fields");
      return;
    }
    
    try {
      await axios.post(`${API}/price-master`, {
        customer_id: priceForm.customer_id,
        buyer_sku_id: priceForm.buyer_sku_id,
        unit_price: parseFloat(priceForm.unit_price),
        currency: priceForm.currency,
        notes: priceForm.notes
      }, { headers: getHeaders() });
      
      toast.success("Price created successfully");
      setShowPriceDialog(false);
      resetPriceForm();
      fetchPrices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create price");
    }
  };

  const handleUpdatePrice = async () => {
    if (!editingPrice) return;
    
    try {
      await axios.put(`${API}/price-master/${editingPrice.id}`, {
        unit_price: parseFloat(priceForm.unit_price),
        notes: priceForm.notes
      }, { headers: getHeaders() });
      
      toast.success("Price updated successfully");
      setShowPriceDialog(false);
      setEditingPrice(null);
      resetPriceForm();
      fetchPrices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update price");
    }
  };

  const handleDeletePrice = async (priceId) => {
    if (!window.confirm("Are you sure you want to deactivate this price?")) return;
    
    try {
      await axios.delete(`${API}/price-master/${priceId}`, { headers: getHeaders() });
      toast.success("Price deactivated");
      fetchPrices();
    } catch (error) {
      toast.error("Failed to delete price");
    }
  };

  const handlePriceBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const data = await uploadWithReceipt(`${API}/price-master/bulk-upload`, formData, {
        axiosConfig: { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } },
        showToast: false,
      });
      
      toast.success((data.message || "Upload complete") + " — Receipt downloaded.");
      if (data.errors?.length > 0) {
        toast.warning(`${data.errors.length} errors occurred — see receipt for details.`);
      }
      fetchPrices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    }
    
    e.target.value = null;
  };

  const downloadPriceTemplate = () => {
    const template = [
      { customer_id: "CUST_0001", buyer_sku_id: "ERW001_TVS", unit_price: 1500.00, currency: "INR", notes: "FY 2026-27" }
    ];
    
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Price Master");
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "price_master_template.xlsx");
  };

  // ─── Realisation Master handlers (Aug 2026) ─────────────────────
  const resetRealisationForm = () => {
    setRealisationForm({
      customer_id: "",
      buyer_sku_id: "",
      realisation_price: "",
      currency: "INR",
      notes: "",
    });
  };

  const openEditRealisation = (entry) => {
    setEditingRealisation(entry);
    setRealisationForm({
      customer_id: entry.customer_id,
      buyer_sku_id: entry.buyer_sku_id,
      realisation_price: String(entry.realisation_price ?? ""),
      currency: entry.currency || "INR",
      notes: entry.notes || "",
    });
    setShowRealisationDialog(true);
  };

  const fetchRealisations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (realisationFilters.customer_id && realisationFilters.customer_id !== "_all") {
        params.append("customer_id", realisationFilters.customer_id);
      }
      params.append("active_only", "true");
      params.append("page_size", String(REALISATIONS_PER_PAGE));
      params.append("page", String(realisationPage));
      const res = await axios.get(`${API}/realisation-master?${params}`, { headers: getHeaders() });
      setRealisations(res.data.entries || []);
      setRealisationsTotal(res.data.total ?? (res.data.entries?.length || 0));
    } catch (error) {
      toast.error("Failed to fetch realisations");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRealisation = async () => {
    if (!realisationForm.customer_id || !realisationForm.buyer_sku_id || !realisationForm.realisation_price) {
      toast.error("Please fill all required fields");
      return;
    }
    try {
      await axios.post(`${API}/realisation-master`, {
        customer_id: realisationForm.customer_id,
        buyer_sku_id: realisationForm.buyer_sku_id,
        realisation_price: parseFloat(realisationForm.realisation_price),
        currency: realisationForm.currency,
        notes: realisationForm.notes,
      }, { headers: getHeaders() });
      toast.success("Realisation entry created");
      setShowRealisationDialog(false);
      resetRealisationForm();
      fetchRealisations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create realisation");
    }
  };

  const handleUpdateRealisation = async () => {
    if (!editingRealisation) return;
    try {
      await axios.put(`${API}/realisation-master/${editingRealisation.id}`, {
        realisation_price: parseFloat(realisationForm.realisation_price),
        notes: realisationForm.notes,
      }, { headers: getHeaders() });
      toast.success("Realisation updated");
      setShowRealisationDialog(false);
      setEditingRealisation(null);
      resetRealisationForm();
      fetchRealisations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update realisation");
    }
  };

  const handleDeleteRealisation = async (entryId) => {
    if (!window.confirm("Deactivate this realisation entry?")) return;
    try {
      await axios.delete(`${API}/realisation-master/${entryId}`, { headers: getHeaders() });
      toast.success("Realisation deactivated");
      fetchRealisations();
    } catch (error) {
      toast.error("Failed to delete realisation");
    }
  };

  const handleRealisationBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const data = await uploadWithReceipt(`${API}/realisation-master/bulk-upload`, formData, {
        axiosConfig: { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } },
        showToast: false,
      });
      toast.success((data.message || "Upload complete") + " — Receipt downloaded.");
      if (data.errors?.length > 0) {
        toast.warning(`${data.errors.length} errors occurred — see receipt for details.`);
      }
      fetchRealisations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    }
    e.target.value = null;
  };

  const downloadRealisationTemplate = () => {
    const template = [{
      customer_id: "FLIPKART",
      buyer_sku_id: "ERW001_TVS",
      realisation_price: 100.00,
      currency: "INR",
      notes: "FY 2026-27 — invoiced ₹500, realisation ₹100",
    }];
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Realisation Master");
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "realisation_master_template.xlsx");
  };

  const exportPrices = async () => {
    // Export always emits the FULL dataset for the current customer
    // filter — NEVER just the visible page. We page through the API at
    // 1000-per-fetch to keep payload sane, then build the workbook.
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (priceFilters.customer_id && priceFilters.customer_id !== "_all") {
        params.append("customer_id", priceFilters.customer_id);
      }
      params.append("active_only", "true");
      params.append("page_size", "1000");
      params.append("page", "1");

      const first = await axios.get(`${API}/price-master?${params}`, { headers: getHeaders() });
      const totalPages = first.data.total_pages ?? 1;
      let all = first.data.prices || [];

      for (let p = 2; p <= totalPages; p++) {
        const pp = new URLSearchParams(params);
        pp.set("page", String(p));
        const r = await axios.get(`${API}/price-master?${pp}`, { headers: getHeaders() });
        all = all.concat(r.data.prices || []);
      }

      if (all.length === 0) {
        toast.error("No prices to export");
        return;
      }

      const exportData = all.map(p => ({
        customer_id: p.customer_id,
        customer_name: p.customer_name,
        buyer_sku_id: p.buyer_sku_id,
        sku_name: p.sku_name,
        unit_price: p.unit_price,
        currency: p.currency,
        effective_from: p.effective_from,
        notes: p.notes
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Price Master");
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([wbout], { type: "application/octet-stream" }), `price_master_${new Date().toISOString().slice(0,10)}.xlsx`);
      toast.success(`Exported ${all.length} price rows`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Export failed");
    } finally {
      setLoading(false);
    }
  };

  const resetPriceForm = () => {
    setPriceForm({
      customer_id: "",
      buyer_sku_id: "",
      unit_price: "",
      currency: "INR",
      notes: ""
    });
  };

  const openEditPrice = (price) => {
    setEditingPrice(price);
    setPriceForm({
      customer_id: price.customer_id,
      buyer_sku_id: price.buyer_sku_id,
      unit_price: price.unit_price.toString(),
      currency: price.currency,
      notes: price.notes || ""
    });
    setShowPriceDialog(true);
  };

  // Filter buyer SKUs by customer
  const getCustomerSkus = (customerId) => {
    // For now, return all buyer SKUs since we don't have brand-customer mapping
    return buyerSkus;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SKU Catalog</h1>
          <p className="text-gray-500 text-sm mt-1">View SKUs and manage customer pricing</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-2xl grid-cols-4">
          <TabsTrigger value="bidso" data-testid="bidso-tab">
            <Layers className="h-4 w-4 mr-2" />
            Bidso SKUs
            <Badge className="ml-2 bg-gray-200 text-gray-700">{bidsoTotal || bidsoSkus.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="buyer" data-testid="buyer-tab">
            <Tag className="h-4 w-4 mr-2" />
            Buyer SKUs
            <Badge className="ml-2 bg-gray-200 text-gray-700">{buyerTotal || buyerSkus.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="prices" data-testid="prices-tab">
            <DollarSign className="h-4 w-4 mr-2" />
            Price Master
            <Badge className="ml-2 bg-gray-200 text-gray-700">{pricesTotal || prices.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="realisations" data-testid="realisations-tab">
            <DollarSign className="h-4 w-4 mr-2" />
            Realisation Master
            <Badge className="ml-2 bg-amber-100 text-amber-700">{realisationsTotal || realisations.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* Bidso SKUs Tab */}
        <TabsContent value="bidso" className="space-y-4">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Layers className="h-5 w-5" />
                  Bidso SKUs (Base Products)
                </CardTitle>
                <Button 
                  variant="outline" 
                  onClick={() => handleDownload("bidso")}
                  disabled={downloading || bidsoSkus.length === 0}
                  data-testid="download-bidso-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {downloading ? "Downloading..." : `Download All (${bidsoTotal})`}
                </Button>
              </div>
              
              {/* Filters */}
              <div className="flex flex-wrap gap-3 mt-4">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-gray-400" />
                </div>
                <Select 
                  value={bidsoFilters.vertical_id || "_all"} 
                  onValueChange={(v) => handleBidsoFilterChange('vertical_id', v === "_all" ? "" : v)}
                >
                  <SelectTrigger className="w-[160px]" data-testid="bidso-vertical-filter">
                    <SelectValue placeholder="All Verticals" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Verticals</SelectItem>
                    {verticals.filter(v => v.id).map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select 
                  value={bidsoFilters.model_id || "_all"} 
                  onValueChange={(v) => handleBidsoFilterChange('model_id', v === "_all" ? "" : v)}
                >
                  <SelectTrigger className="w-[160px]" data-testid="bidso-model-filter">
                    <SelectValue placeholder="All Models" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Models</SelectItem>
                    {getFilteredModels(bidsoFilters.vertical_id).filter(m => m.id).map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search SKU ID..."
                    value={bidsoFilters.search}
                    onChange={(e) => handleBidsoFilterChange('search', e.target.value)}
                    className="pl-10"
                    data-testid="bidso-search-input"
                  />
                </div>
                
                {(bidsoFilters.vertical_id || bidsoFilters.model_id || bidsoFilters.search) && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setBidsoFilters({vertical_id: "", brand_id: "", model_id: "", search: ""});
                      setBidsoPage(1);
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bidso SKU ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Vertical</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : bidsoSkus.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                        No Bidso SKUs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    bidsoSkus.map(sku => (
                      <TableRow key={sku.id} data-testid={`bidso-row-${sku.bidso_sku_id}`}>
                        <TableCell className="font-mono font-medium">{sku.bidso_sku_id}</TableCell>
                        <TableCell className="max-w-xs truncate">{sku.name || '-'}</TableCell>
                        <TableCell>{sku.vertical?.name || '-'}</TableCell>
                        <TableCell>{sku.model?.name || '-'}</TableCell>
                        <TableCell>
                          <Badge className={sku.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                            {sku.status || 'ACTIVE'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              
              {/* Pagination for Bidso SKUs */}
              <Pagination
                currentPage={bidsoPage}
                totalPages={bidsoTotalPages}
                totalItems={bidsoTotal}
                pageSize={bidsoPageSize}
                onPageChange={handleBidsoPageChange}
                onPageSizeChange={handleBidsoPageSizeChange}
                loading={loading}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Buyer SKUs Tab */}
        <TabsContent value="buyer" className="space-y-4">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Tag className="h-5 w-5" />
                  Buyer SKUs (Customer-Specific)
                </CardTitle>
                <Button 
                  variant="outline" 
                  onClick={() => handleDownload("buyer")}
                  disabled={downloading || buyerTotal === 0}
                  data-testid="download-buyer-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {downloading ? "Downloading..." : `Download All (${buyerTotal})`}
                </Button>
              </div>
              
              {/* Filters */}
              <div className="flex flex-wrap gap-3 mt-4">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-gray-400" />
                </div>
                <Select 
                  value={buyerFilters.vertical_id || "_all"} 
                  onValueChange={(v) => handleBuyerFilterChange('vertical_id', v === "_all" ? "" : v)}
                >
                  <SelectTrigger className="w-[150px]" data-testid="buyer-vertical-filter">
                    <SelectValue placeholder="All Verticals" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Verticals</SelectItem>
                    {verticals.filter(v => v.id).map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select 
                  value={buyerFilters.brand_id || "_all"} 
                  onValueChange={(v) => handleBuyerFilterChange('brand_id', v === "_all" ? "" : v)}
                >
                  <SelectTrigger className="w-[150px]" data-testid="buyer-brand-filter">
                    <SelectValue placeholder="All Brands" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Brands</SelectItem>
                    {brands.filter(b => b.id).map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select 
                  value={buyerFilters.model_id || "_all"} 
                  onValueChange={(v) => handleBuyerFilterChange('model_id', v === "_all" ? "" : v)}
                >
                  <SelectTrigger className="w-[150px]" data-testid="buyer-model-filter">
                    <SelectValue placeholder="All Models" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Models</SelectItem>
                    {getFilteredModels(buyerFilters.vertical_id).filter(m => m.id).map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select 
                  value={buyerFilters.buyer_id || "_all"} 
                  onValueChange={(v) => handleBuyerFilterChange('buyer_id', v === "_all" ? "" : v)}
                >
                  <SelectTrigger className="w-[180px]" data-testid="buyer-buyer-filter">
                    <SelectValue placeholder="All Buyers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Buyers</SelectItem>
                    {buyers.filter(b => b.id).map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search SKU ID..."
                    value={buyerFilters.search}
                    onChange={(e) => handleBuyerFilterChange('search', e.target.value)}
                    className="pl-10"
                    data-testid="buyer-search-input"
                  />
                </div>
                
                {(buyerFilters.vertical_id || buyerFilters.brand_id || buyerFilters.model_id || buyerFilters.buyer_id || buyerFilters.search) && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setBuyerFilters({vertical_id: "", brand_id: "", model_id: "", buyer_id: "", search: ""});
                      setBuyerPage(1);
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU ID</TableHead>
                    <TableHead>Vertical</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : buyerSkus.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                        No Buyer SKUs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    buyerSkus.map(sku => (
                      <TableRow key={sku.id} data-testid={`buyer-row-${sku.sku_id}`}>
                        <TableCell className="font-mono font-medium">{sku.sku_id}</TableCell>
                        <TableCell>{getVerticalName(sku.vertical_id)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{getBrandName(sku.brand_id)}</Badge>
                        </TableCell>
                        <TableCell>{getModelName(sku.model_id)}</TableCell>
                        <TableCell>
                          <Badge className={sku.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                            {sku.status || 'ACTIVE'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              
              {/* Pagination for Buyer SKUs */}
              <Pagination
                currentPage={buyerPage}
                totalPages={buyerTotalPages}
                totalItems={buyerTotal}
                pageSize={buyerPageSize}
                onPageChange={handleBuyerPageChange}
                onPageSizeChange={handleBuyerPageSizeChange}
                loading={loading}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Price Master Tab */}
        <TabsContent value="prices">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Price Master
                </CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={downloadPriceTemplate}>
                    <Download className="h-4 w-4 mr-2" />
                    Template
                  </Button>
                  <input type="file" ref={priceFileRef} onChange={handlePriceBulkUpload} accept=".xlsx,.xls" className="hidden" />
                  <Button variant="outline" size="sm" onClick={() => priceFileRef.current.click()}>
                    <Upload className="h-4 w-4 mr-2" />
                    Bulk Upload
                  </Button>
                  <Button variant="outline" size="sm" onClick={exportPrices}>
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                  <Dialog open={showPriceDialog} onOpenChange={(open) => { 
                    setShowPriceDialog(open); 
                    if (!open) { resetPriceForm(); setEditingPrice(null); }
                  }}>
                    <DialogTrigger asChild>
                      <PermButton entity="BuyerSKU" action="UPDATE" size="sm" data-testid="add-price-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Price
                      </PermButton>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle className="font-bold uppercase">
                          {editingPrice ? "Edit Price" : "Add New Price"}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div>
                          <Label className="text-xs uppercase">Customer *</Label>
                          <Select 
                            value={priceForm.customer_id} 
                            onValueChange={(v) => setPriceForm({...priceForm, customer_id: v})}
                            disabled={!!editingPrice}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select customer" />
                            </SelectTrigger>
                            <SelectContent>
                              {buyers.filter(b => b.id || b.customer_code).map(b => (
                                <SelectItem key={b.id} value={b.customer_code || b.id}>
                                  {b.name} ({b.customer_code || b.id})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label className="text-xs uppercase">Buyer SKU *</Label>
                          <Select 
                            value={priceForm.buyer_sku_id || undefined} 
                            onValueChange={(v) => setPriceForm({...priceForm, buyer_sku_id: v})}
                            disabled={!!editingPrice}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select SKU" />
                            </SelectTrigger>
                            <SelectContent>
                              {buyerSkus.filter(sku => sku.buyer_sku_id || sku.sku_id).slice(0, 200).map(sku => (
                                <SelectItem key={sku.buyer_sku_id || sku.sku_id} value={sku.buyer_sku_id || sku.sku_id}>
                                  {sku.buyer_sku_id || sku.sku_id} - {sku.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label className="text-xs uppercase">Unit Price (₹) *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={priceForm.unit_price}
                            onChange={(e) => setPriceForm({...priceForm, unit_price: e.target.value})}
                            placeholder="0.00"
                            className="font-mono"
                          />
                        </div>
                        
                        <div>
                          <Label className="text-xs uppercase">Notes</Label>
                          <Input
                            value={priceForm.notes}
                            onChange={(e) => setPriceForm({...priceForm, notes: e.target.value})}
                            placeholder="FY 2026-27 pricing"
                          />
                        </div>
                        
                        <div className="flex justify-end gap-2 pt-4">
                          <Button variant="outline" onClick={() => { setShowPriceDialog(false); resetPriceForm(); setEditingPrice(null); }}>
                            Cancel
                          </Button>
                          <Button onClick={editingPrice ? handleUpdatePrice : handleCreatePrice}>
                            {editingPrice ? "Update Price" : "Add Price"}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex gap-4 mb-4">
                <Select 
                  value={priceFilters.customer_id || "_all"} 
                  onValueChange={(v) => { setPricePage(1); setPriceFilters({...priceFilters, customer_id: v}); }}
                >
                  <SelectTrigger className="w-[250px]">
                    <SelectValue placeholder="All Customers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Customers</SelectItem>
                    {buyers.filter(b => b.id || b.customer_code).map(b => (
                      <SelectItem key={b.id} value={b.customer_code || b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Price Table */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Buyer SKU</TableHead>
                    <TableHead>SKU Name</TableHead>
                    <TableHead className="text-right">Unit Price</TableHead>
                    <TableHead>Effective From</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">Loading...</TableCell>
                    </TableRow>
                  ) : prices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No prices found. Click &quot;Add Price&quot; to create one.
                      </TableCell>
                    </TableRow>
                  ) : (
                    prices.map(price => (
                      <TableRow key={price.id}>
                        <TableCell>
                          <div className="font-medium">{price.customer_name}</div>
                          <div className="text-xs text-muted-foreground font-mono">{price.customer_id}</div>
                        </TableCell>
                        <TableCell className="font-mono">{price.buyer_sku_id}</TableCell>
                        <TableCell className="text-sm">{price.sku_name}</TableCell>
                        <TableCell className="text-right font-mono font-medium">
                          ₹{price.unit_price?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {price.effective_from ? new Date(price.effective_from).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[150px] truncate">
                          {price.notes || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openEditPrice(price)} title="Edit">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeletePrice(price.id)} title="Delete">
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {/* Pagination — server-side paged at 100/page. Always visible
                  when there's data so users see which page they're on. */}
              {pricesTotal > 0 && (
                <div className="flex items-center justify-between p-3 border-t text-sm" data-testid="price-master-pagination">
                  <div className="text-zinc-500">
                    Page <strong>{pricePage}</strong> of <strong>{pricesTotalPages}</strong>
                    {" "}· {PRICES_PER_PAGE} per page · {pricesTotal} total
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pricePage === 1}
                      onClick={() => setPricePage(1)}
                      data-testid="price-pagination-first"
                    >First</Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pricePage === 1}
                      onClick={() => setPricePage((p) => Math.max(1, p - 1))}
                      data-testid="price-pagination-prev"
                    >Previous</Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pricePage >= pricesTotalPages}
                      onClick={() => setPricePage((p) => Math.min(pricesTotalPages, p + 1))}
                      data-testid="price-pagination-next"
                    >Next</Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pricePage >= pricesTotalPages}
                      onClick={() => setPricePage(pricesTotalPages)}
                      data-testid="price-pagination-last"
                    >Last</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Realisation Master Tab (Aug 2026) */}
        <TabsContent value="realisations" className="space-y-4">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Realisation Master
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Customer × SKU economic realisation prices — the true consideration per unit.
                    Used for revenue + margin on Pulse and BOM-lookup. Only marketplace SKUs where
                    invoiced ≠ realised need entries here. Price Master remains untouched for invoicing.
                  </p>
                </div>
                <div className="flex gap-2">
                  <input
                    type="file"
                    ref={realisationFileRef}
                    className="hidden"
                    accept=".xlsx,.xls"
                    onChange={handleRealisationBulkUpload}
                    data-testid="realisation-bulk-upload-input"
                  />
                  <Button
                    variant="outline"
                    onClick={downloadRealisationTemplate}
                    data-testid="download-realisation-template-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Template
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => realisationFileRef.current?.click()}
                    data-testid="realisation-bulk-upload-btn"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Bulk Upload
                  </Button>
                  <Dialog open={showRealisationDialog} onOpenChange={setShowRealisationDialog}>
                    <DialogTrigger asChild>
                      <Button
                        onClick={() => { resetRealisationForm(); setEditingRealisation(null); }}
                        data-testid="add-realisation-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Realisation
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>
                          {editingRealisation ? "Edit Realisation Entry" : "Add Realisation Entry"}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div>
                          <Label className="text-xs uppercase">Customer *</Label>
                          <Select
                            value={realisationForm.customer_id}
                            onValueChange={(v) => setRealisationForm({ ...realisationForm, customer_id: v })}
                            disabled={!!editingRealisation}
                          >
                            <SelectTrigger data-testid="realisation-customer-select">
                              <SelectValue placeholder="Select customer" />
                            </SelectTrigger>
                            <SelectContent>
                              {buyers.filter(b => b.id || b.customer_code).map(b => (
                                <SelectItem key={b.id} value={b.customer_code || b.id}>{b.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs uppercase">Buyer SKU *</Label>
                          <Input
                            value={realisationForm.buyer_sku_id}
                            onChange={(e) => setRealisationForm({ ...realisationForm, buyer_sku_id: e.target.value })}
                            placeholder="e.g. ERW001_TVS"
                            className="font-mono"
                            disabled={!!editingRealisation}
                            data-testid="realisation-buyer-sku-input"
                          />
                        </div>
                        <div>
                          <Label className="text-xs uppercase">Realisation Price (₹) *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={realisationForm.realisation_price}
                            onChange={(e) => setRealisationForm({ ...realisationForm, realisation_price: e.target.value })}
                            placeholder="0.00"
                            className="font-mono"
                            data-testid="realisation-price-input"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            The full economic realisation the user enters — Playfield stores this as-is.
                          </p>
                        </div>
                        <div>
                          <Label className="text-xs uppercase">Notes</Label>
                          <Input
                            value={realisationForm.notes}
                            onChange={(e) => setRealisationForm({ ...realisationForm, notes: e.target.value })}
                            placeholder="e.g. Flipkart FY2026-27 — invoiced ₹500, realisation ₹100"
                            data-testid="realisation-notes-input"
                          />
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                          <Button
                            variant="outline"
                            onClick={() => { setShowRealisationDialog(false); resetRealisationForm(); setEditingRealisation(null); }}
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={editingRealisation ? handleUpdateRealisation : handleCreateRealisation}
                            data-testid="realisation-save-btn"
                          >
                            {editingRealisation ? "Update" : "Add Realisation"}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 mb-4">
                <Select
                  value={realisationFilters.customer_id || "_all"}
                  onValueChange={(v) => { setRealisationPage(1); setRealisationFilters({ ...realisationFilters, customer_id: v === "_all" ? "" : v }); }}
                >
                  <SelectTrigger className="w-[250px]" data-testid="realisation-customer-filter">
                    <SelectValue placeholder="All Customers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Customers</SelectItem>
                    {buyers.filter(b => b.id || b.customer_code).map(b => (
                      <SelectItem key={b.id} value={b.customer_code || b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Table data-testid="realisation-master-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Buyer SKU</TableHead>
                    <TableHead>SKU Name</TableHead>
                    <TableHead className="text-right">Realisation Price</TableHead>
                    <TableHead>Effective From</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">Loading...</TableCell>
                    </TableRow>
                  ) : realisations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No realisation entries yet. Add one for customers whose invoiced price differs from the true realisation.
                      </TableCell>
                    </TableRow>
                  ) : (
                    realisations.map(entry => (
                      <TableRow key={entry.id} data-testid={`realisation-row-${entry.id}`}>
                        <TableCell>
                          <div className="font-medium">{entry.customer_name}</div>
                          <div className="text-xs text-muted-foreground font-mono">{entry.customer_id}</div>
                        </TableCell>
                        <TableCell className="font-mono">{entry.buyer_sku_id}</TableCell>
                        <TableCell className="text-sm">{entry.sku_name}</TableCell>
                        <TableCell className="text-right font-mono font-medium">
                          ₹{entry.realisation_price?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {entry.effective_from ? new Date(entry.effective_from).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                          {entry.notes || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openEditRealisation(entry)} title="Edit" data-testid={`realisation-edit-${entry.id}`}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteRealisation(entry.id)} title="Delete" data-testid={`realisation-delete-${entry.id}`}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {realisationsTotal > 0 && (
                <div className="flex items-center justify-between p-3 border-t text-sm" data-testid="realisation-master-pagination">
                  <div className="text-zinc-500">
                    Page <strong>{realisationPage}</strong> of <strong>{realisationTotalPages}</strong>
                    {" "}· {REALISATIONS_PER_PAGE} per page · {realisationsTotal} total
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={realisationPage === 1} onClick={() => setRealisationPage(1)}>First</Button>
                    <Button variant="outline" size="sm" disabled={realisationPage === 1} onClick={() => setRealisationPage((p) => Math.max(1, p - 1))}>Previous</Button>
                    <Button variant="outline" size="sm" disabled={realisationPage >= realisationTotalPages} onClick={() => setRealisationPage((p) => Math.min(realisationTotalPages, p + 1))}>Next</Button>
                    <Button variant="outline" size="sm" disabled={realisationPage >= realisationTotalPages} onClick={() => setRealisationPage(realisationTotalPages)}>Last</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DemandSKUView;
